import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { HighlightText } from "@/components/highlight-text"
import { slideAlternatingBlocks, slideInFrom, sectionHeaderClasses } from "@/lib/section-slide"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

type Category = "all" | "ai" | "video" | "auto" | "edge"

const filters: { id: Category; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ai", label: "AI" },
  { id: "video", label: "Video" },
  { id: "auto", label: "Auto" },
  { id: "edge", label: "Edge" },
]

type ProjectCategory = Exclude<Category, "all">

const VIDEO_MICROPROCESSOR = "/videos/microprocessor-circuit.mp4"
const VIDEO_AI_CHIP = "/videos/ai-chip.mp4"
const VIDEO_TRUCK_TRAILER = "/videos/truck-trailer.mp4"
const VIDEO_CAMERA_LENS = "/videos/camera-lens.mp4"
const VIDEO_ANIMATION_DATA = "/videos/animation-data.mp4"

const projects: Array<{
  id: string
  title: string
  categories: ProjectCategory[]
  featured?: boolean
  description: string
  tags: string[]
  meta: string
  video?: string
}> = [
  {
    id: "4k-video-surveillance",
    title: "4K Video Surveillance SoC",
    categories: ["video"],
    featured: true,
    description:
      "Full RTL-to-GDSII implementation of a large-die 4K video surveillance SoC, including power/PAD/BUMP planning and RDL routing.",
    tags: ["TSMC 16nm", "650mm²", "RTL → GDSII"],
    meta: "TSMC 16nm · 650mm²",
    video: VIDEO_MICROPROCESSOR,
  },
  {
    id: "edge-ai-accelerator",
    title: "Edge AI Accelerator",
    categories: ["ai", "edge"],
    description:
      "Compact AI inference accelerator physical design from floorplan through signoff on advanced FinFET node.",
    tags: ["TSMC 16FFC+", "35mm²", "Low-power PD"],
    meta: "TSMC 16FFC+ · 35mm²",
    video: VIDEO_AI_CHIP,
  },
  {
    id: "pro-video-camera",
    title: "Pro Video Camera SoC",
    categories: ["video"],
    description:
      "Professional broadcast camera SoC implementation with mixed-signal IP integration and timing closure.",
    tags: ["TSMC 28nm", "125mm²", "Mixed-signal"],
    meta: "TSMC 28nm · 125mm²",
    video: VIDEO_CAMERA_LENS,
  },
  {
    id: "adas-rf-sensor",
    title: "ADAS RF Sensor SoC",
    categories: ["auto"],
    description:
      "Automotive radar sensor SoC with RF/analog floorplanning and full-chip physical verification ownership.",
    tags: ["GF 22FDX+", "32mm²", "RF/analog"],
    meta: "GF 22FDX+ · 32mm²",
    video: VIDEO_TRUCK_TRAILER,
  },
  {
    id: "video-edge-soc",
    title: "Video Edge SoC",
    categories: ["video", "edge"],
    description:
      "Large-scale edge video processing SoC with hierarchical block-level implementation.",
    tags: ["GF 22FDX+", "~400mm²", "Low-power PD"],
    meta: "GF 22FDX+ · ~400mm²",
    video: VIDEO_ANIMATION_DATA,
  },
]

function getMatchedProjects(filter: Category) {
  return filter === "all"
    ? projects
    : projects.filter((project) => project.categories.includes(filter))
}

function getDefaultFeaturedId(filter: Category) {
  const matched = getMatchedProjects(filter)
  return (
    matched.find((project) => project.featured)?.id ??
    matched[0]?.id ??
    null
  )
}

function getFeaturedVideo(projectId: string | null, filter: Category) {
  if (!projectId) return null

  if (projectId === "edge-ai-accelerator") return VIDEO_AI_CHIP
  if (projectId === "adas-rf-sensor") return VIDEO_TRUCK_TRAILER
  if (projectId === "pro-video-camera") return VIDEO_CAMERA_LENS
  if (projectId === "video-edge-soc") return VIDEO_ANIMATION_DATA

  // Default featured under All → microprocessor circuit video
  if (filter === "all" && projectId === "4k-video-surveillance") {
    return VIDEO_MICROPROCESSOR
  }

  if (projectId === "4k-video-surveillance") return VIDEO_MICROPROCESSOR

  return projects.find((project) => project.id === projectId)?.video ?? null
}

export function ProofSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const filtersRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const featuredRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const accentLineRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [activeFilter, setActiveFilter] = useState<Category>("all")
  const [selectedId, setSelectedId] = useState<string | null>("4k-video-surveillance")
  const skipFilterAnimation = useRef(true)

  const matched = useMemo(() => getMatchedProjects(activeFilter), [activeFilter])

  const featured = useMemo(() => {
    const selected = matched.find((project) => project.id === selectedId)
    if (selected) return selected
    return matched.find((project) => project.id === getDefaultFeaturedId(activeFilter)) ?? matched[0]
  }, [matched, selectedId, activeFilter])

  const list = useMemo(
    () => matched.filter((project) => project.id !== featured?.id),
    [matched, featured],
  )

  const backgroundVideo = useMemo(
    () => getFeaturedVideo(featured?.id ?? null, activeFilter),
    [featured, activeFilter],
  )

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !filtersRef.current || !contentRef.current) return

    const ctx = gsap.context(() => {
      slideInFrom(headerRef.current, "right")
      slideInFrom(filtersRef.current, "right", { delay: 0.1 })
      slideInFrom(featuredRef.current, "left", { delay: 0.15 })
      slideAlternatingBlocks(listRef.current, "[data-proof-item]", "right", { delay: 0.2 })

      if (accentLineRef.current) {
        gsap.fromTo(
          accentLineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 1,
            ease: "power3.inOut",
            scrollTrigger: {
              trigger: contentRef.current,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const animateFeatureChange = useCallback(() => {
    if (!featuredRef.current || !listRef.current) return

    const items = listRef.current.querySelectorAll("[data-proof-item]")

    gsap.fromTo(
      featuredRef.current,
      { x: -24, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.55, ease: "power3.out" },
    )

    if (accentLineRef.current) {
      gsap.fromTo(
        accentLineRef.current,
        { scaleX: 0 },
        { scaleX: 1, duration: 0.75, ease: "power3.inOut", delay: 0.1 },
      )
    }

    if (items.length) {
      gsap.fromTo(
        items,
        { x: 24, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: "power3.out", delay: 0.08 },
      )
    }
  }, [])

  const handleFilterChange = useCallback(
    (filter: Category) => {
      if (filter === activeFilter) return
      setActiveFilter(filter)
      setSelectedId(getDefaultFeaturedId(filter))
    },
    [activeFilter],
  )

  const handleSelectProject = useCallback(
    (id: string) => {
      if (id === featured?.id) return
      setSelectedId(id)
    },
    [featured],
  )

  useEffect(() => {
    if (skipFilterAnimation.current) {
      skipFilterAnimation.current = false
      return
    }
    animateFeatureChange()
  }, [activeFilter, selectedId, animateFeatureChange])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !backgroundVideo) return
    video.load()
    void video.play().catch(() => {
      // Autoplay can be blocked; muted + playsInline usually allows it.
    })
  }, [backgroundVideo])

  return (
    <section ref={sectionRef} id="proof" className="relative border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12">
      <div ref={headerRef} className={cn("mb-10 max-w-3xl", sectionHeaderClasses("right"))}>
        <span className="mb-4 block font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Use cases
        </span>
        <h2 className="mt-4 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-7xl">
          <HighlightText>Of The Many Designs</HighlightText>
          <br />
          We&apos;ve Taped-Out
          <br />
          A Few Examples,
        </h2>
        <p className="mt-6 font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
          Selected engagements across AI, video, automotive and edge — from a much broader tape-out portfolio.
        </p>
      </div>

      <div ref={filtersRef} className={cn("mb-10 flex flex-wrap justify-end gap-3", sectionHeaderClasses("right"))}>
        {filters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            onClick={() => handleFilterChange(filter.id)}
            className={cn(
              "border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300",
              activeFilter === filter.id
                ? "border-accent bg-accent text-accent-foreground"
                : "border-accent/50 bg-transparent text-foreground hover:border-accent hover:bg-accent/10",
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <div ref={contentRef} className="grid gap-px border border-border/30 bg-border/30 md:grid-cols-[1.4fr_1fr]">
        {featured && (
          <article
            ref={featuredRef}
            className="relative isolate min-h-[320px] overflow-hidden bg-card/90 p-8 md:min-h-[420px] md:p-10"
          >
            {backgroundVideo && (
              <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
                <video
                  key={backgroundVideo}
                  ref={videoRef}
                  className="absolute inset-0 h-full w-full scale-105 object-cover"
                  src={backgroundVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-background/92 via-background/78 to-background/55" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-background/40" />
                <div className="absolute inset-0 bg-background/25 mix-blend-multiply" />
              </div>
            )}

            <div className="absolute inset-x-0 top-0 z-10 h-1 overflow-hidden bg-border/30">
              <div ref={accentLineRef} className="h-full w-full origin-left scale-x-0 bg-accent" />
            </div>

            <div className="relative z-10">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Featured</span>
              <h3 className="mt-4 font-[var(--font-bebas)] text-3xl tracking-tight uppercase text-foreground md:text-4xl lg:text-5xl">
                {featured.title}
              </h3>
              <p className="mt-6 max-w-xl font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
                {featured.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-2">
                {featured.tags.map((tag) => (
                  <span
                    key={tag}
                    className="border border-border/50 bg-background/40 px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground backdrop-blur-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>
        )}

        <div ref={listRef} className="flex flex-col bg-card/90">
          {list.map((project) => (
            <button
              key={project.id}
              type="button"
              data-proof-item
              onClick={() => handleSelectProject(project.id)}
              className={cn(
                "group flex flex-col items-start border-b border-border/30 px-6 py-6 text-left transition-colors duration-300 last:border-b-0 md:px-8 md:py-8",
                "hover:bg-accent/5",
              )}
            >
              <h4 className="font-[var(--font-bebas)] text-xl tracking-tight uppercase transition-colors duration-300 group-hover:text-accent md:text-2xl">
                {project.title}
              </h4>
              <p className="mt-2 font-mono text-[10px] text-muted-foreground transition-colors duration-300 group-hover:text-accent/70">
                {project.meta}
              </p>
              <span className="mt-3 inline-block h-px w-0 bg-accent transition-all duration-500 group-hover:w-12" />
            </button>
          ))}

          {list.length === 0 && (
            <div data-proof-item className="flex flex-1 items-center px-6 py-10 md:px-8">
              <p className="font-mono text-xs text-muted-foreground">No additional engagements in this category.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
